import { AppTourService } from '../../services/app-tour/app-tour.service';
import { Component, HostListener, Input, OnDestroy, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { IIngredientCategory } from '../../interfaces/database/database.interface';
import { IngredientsAction, IngredientState } from '../../models/ingredient';
import { takeWhile } from 'rxjs/operators';
import { IMacronutrients } from '../../interfaces/nutrients';
import { CalorieTrackingNamespace, MealPlanBuilderAction, MealPlanBuilderState, NutrientTableNamespace } from '../../models/meal-plan-builder';
import { Observable } from 'rxjs';
import { DeviceTypeEnum } from '../../enums/utils';
import { UtilitiesService } from '../../services/utilities-service/utilities.service';
import { IMealPlan, IPlanPeriod } from 'src/app/models/meal-plans';
import { PlanPeriodClass } from 'src/app/class/meal-plan/plan-period.class';

@Component({
  selector: 'app-c-nutrient-table',
  templateUrl: './c-nutrient-table.component.html',
  styleUrls: ['./c-nutrient-table.component.scss']
})
export class CNutrientTableComponent implements OnInit, OnDestroy {

  @Select(MealPlanBuilderState.getCalorieTracking)
  getCalorieTracking$: Observable<Partial<CalorieTrackingNamespace.ICalorieTracking> | null>;

  destroy = false;
  ingredientsCategory: IIngredientCategory[] = [];
  mapIngredients: { [key: string]: number } = {};

  @Input() width = '100%';
  @Input() height = '50vh';
  @Input() macronutrientsBase: IMacronutrients | undefined;
  @Input() disabled: boolean;

  @Input() planPeriod: PlanPeriodClass;

  macronutrientsSummary!: IMacronutrients;
  macronutrientsAdequacy!: IMacronutrients;

  edited: boolean = false;



  timeOutCheck: any;
  timeOutResize: any;

  isDeviceMobile: boolean;


  statusColors = {
    regular: '#99bbbf',
    success: 'rgb(22 101 52)',
    exception: 'rgb(239 68 68)'
  };


  statusClass = {
    regular: '',
    success: 'tw-text-green-800',
    exception: 'tw-text-red-500'
  }

  constructor(
    private store: Store,
    private utilitiesService: UtilitiesService,
    private appTourService: AppTourService
  ) {
    this.init();
  }

  init(): void {
    const initMacros: IMacronutrients = {
      proteins: 0,
      lipids: 0,
      carbohydrates: 0,
      calories: 0,
      fiber: 0
    };
    this.macronutrientsSummary = { ...initMacros };
    this.macronutrientsAdequacy = { ...initMacros };
    this.isDeviceMobile = this.getDeviceMobile();
    this.calcAdequacy();
  }


  ngOnDestroy(): void {
    this.destroy = true;
  }

  async ngOnInit(): Promise<void> {
    this.mapIngredients = this.planPeriod.metadata?.nutrientTable || {};

    const categories = this.store.selectSnapshot(IngredientState.getIngredientCategories);

    this.store.select(IngredientState.getIngredientCategories)
      .pipe(takeWhile(() => !this.destroy))
      .subscribe((categoriesState: IIngredientCategory[]) => {
        this.ingredientsCategory = categoriesState;
        this.ingredientsCategory.forEach((category: IIngredientCategory) => {
          this.mapIngredients[category._id] = this.mapIngredients[category._id] || 0;
        });

        if (!this.store.selectSnapshot(MealPlanBuilderState.getNutrientTable)) {
          this.store.dispatch(new MealPlanBuilderAction.PatchNutrientTable(this.mapIngredients));
        }

        this.getCategoriesValues();
      });

    if (categories.length === 0) {
      await this.store.dispatch(new IngredientsAction.GetCategories());
    }

    this.listeners();
  }


  private listeners(): void {
    this.getCalorieTracking$.pipe(takeWhile(() => !this.destroy)).subscribe({
      next: (calorieTracking: Partial<CalorieTrackingNamespace.ICalorieTracking> | null): void => {
        setTimeout(() => {
          this.calcTotalMacronutrients();
          this.calcAdequacy();
          this.getCategoriesValues();
          this.getMacronutrientSmaeTable();
        }, 300);
      }
    });

    this.appTourService.events.pipe(takeWhile(() => !this.destroy)).subscribe({
      next: (event: string) => {
        switch (event) {
          case 'setQuantityIngredient': {
            this.mapIngredients[this.ingredientsCategory[0]._id] = 4;
            this.calcValues();
          }
        }
      }
    });
  }

  calcValues(edited = false): void {

    if (!this.edited)
      this.edited = edited;

    this.planPeriod.metadata.nutrientTable = this.mapIngredients;

    if (this.timeOutCheck) {
      clearTimeout(this.timeOutCheck);
    }
    this.timeOutCheck = setTimeout(() => {
      this.calcTotalMacronutrients();
      this.calcAdequacy();
      this.getCategoriesValues();
      this.store.dispatch(new MealPlanBuilderAction.PatchNutrientTable(this.mapIngredients));
      this.getMacronutrientSmaeTable();
    }, 300);
  }

  calcTotalMacronutrients(): void {
    this.macronutrientsSummary = {
      proteins: 0,
      lipids: 0,
      carbohydrates: 0,
      calories: 0,
      fiber: 0
    };
    this.ingredientsCategory.forEach((category: IIngredientCategory) => {
      const amount = this.mapIngredients[category._id];
      if (amount) {
        this.macronutrientsSummary.proteins += category.smae.proteins * amount;
        this.macronutrientsSummary.lipids += category.smae.lipids * amount;
        this.macronutrientsSummary.carbohydrates += category.smae.carbohydrates * amount;
        this.macronutrientsSummary.calories += category.smae.calories * amount;
        this.macronutrientsSummary.fiber += category.smae.fiber * amount;
      }
    });
  }

  calcAdequacy(): void {
    if (this.macronutrientsBase !== undefined) {
      this.macronutrientsAdequacy.proteins = this.macronutrientsSummary.proteins / this.macronutrientsBase.proteins;
      this.macronutrientsAdequacy.lipids = this.macronutrientsSummary.lipids / this.macronutrientsBase.lipids;
      this.macronutrientsAdequacy.carbohydrates = this.macronutrientsSummary.carbohydrates / this.macronutrientsBase.carbohydrates;
      this.macronutrientsAdequacy.calories = this.macronutrientsSummary.calories / this.macronutrientsBase.calories;
      this.macronutrientsAdequacy.fiber = this.macronutrientsSummary.fiber / this.macronutrientsBase.fiber;
    }
  }

  getCategoriesValues(): void {
    const values: NutrientTableNamespace.INutrientTableWithName = {};
    for (const prop in this.mapIngredients) {
      if (this.mapIngredients.hasOwnProperty(prop)) {
        values[prop] = {
          valueSmaeTable: this.mapIngredients[prop],
          name: this.ingredientsCategory.find((category: IIngredientCategory) => category._id === prop)?.name || ''
        };
      }
    }
    this.ordenByValueSmaeTable();
    this.store.dispatch(new MealPlanBuilderAction.PatchNutrientTableWithName(values));
  }


  ordenByValueSmaeTable() {
    if (!this.edited) {
      this.ingredientsCategory.sort((a, b) => {
        const aValue = this.mapIngredients[a._id] || 0;
        const bValue = this.mapIngredients[b._id] || 0;
        if (aValue === 0 && bValue !== 0) {
          return 1;
        } else if (aValue !== 0 && bValue === 0) {
          return -1;
        } else {
          return 0;
        }
      });
    }
  }


  getMacronutrientSmaeTable(): void {
    this.store.dispatch(new MealPlanBuilderAction.PatchMacronutrientsSmaeTable(
      {
        summary: this.macronutrientsSummary,
        adequacy: this.macronutrientsAdequacy
      }
    ));
  }

  getDeviceMobile(): boolean {
    const deviceType: DeviceTypeEnum = this.utilitiesService.getDeviceType();
    return deviceType === DeviceTypeEnum.MOBILE;
  }


  getStatus(value: number): string {
    if (value < 0.95) {
      return 'regular';
    } else if (value >= 0.95 && value <= 1.05) {
      return 'success';
    } else {
      return 'exception';
    }
  }


  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (this.timeOutResize) {
      clearTimeout(this.timeOutResize);
    }
    this.timeOutResize = setTimeout(() => {
      this.isDeviceMobile = this.getDeviceMobile();
    }, 300);
  }
}